import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton } from '../componentes/ui/basicos';
import { Dialogo } from '../componentes/ui/dialogo';
import { Campo, Selector } from '../componentes/ui/formulario';
import { Cargando, useTextoDeError } from '../componentes/ui/estados';
import {
  CajaDelEfectivo,
  cajaDelEfectivo,
  useCajasAbiertas,
  type CajaFija,
} from '../componentes/CajaDelEfectivo';

/** A quién se le paga: lo mínimo para titular el diálogo y pedir sus facturas. */
export interface ProveedorAPagar {
  readonly terceroId: string;
  readonly razonSocial: string;
}

interface ValorEnCartera {
  readonly id: string;
  readonly numero: string;
  readonly banco: string;
  readonly fechaDeCobro: string;
  readonly importe: string;
}

interface FacturaPendiente {
  readonly movimientoId: string;
  readonly concepto: string | null;
  readonly vencimiento: string | null;
  readonly pendiente: string;
}

const MEDIOS = ['efectivo', 'transferencia', 'cheque', 'debito', 'credito', 'otro'] as const;

function conDosDecimales(importe: string): string {
  const [entera, fraccion = ''] = importe.split('.');
  return `${entera ?? '0'}.${fraccion.padEnd(2, '0').slice(0, 2)}`;
}

/** Suma de textos con dos decimales, sin pasar por coma flotante: es plata. */
function enCentavos(valor: string): number {
  const [entera = '0', fraccion = ''] = valor.replace(',', '.').split('.');
  const total = Number(entera) * 100 + Number(fraccion.padEnd(2, '0').slice(0, 2));
  return Number.isNaN(total) ? 0 : total;
}

function deCentavos(centavos: number): string {
  const signo = centavos < 0 ? '-' : '';
  const absoluto = Math.abs(centavos);
  return `${signo}${String(Math.floor(absoluto / 100))}.${String(absoluto % 100).padStart(2, '0')}`;
}

/**
 * La orden de pago a un proveedor: con qué se le paga y qué facturas cancela.
 *
 * Vivía adentro de Pagos; desde E37 la usa también la caja, con el turno ya puesto, y por eso es
 * un componente. Dos copias del mismo formulario se separan a la primera corrección.
 */
export function DialogoDePago({
  proveedor: pagando,
  onCerrar,
  cajaFija,
}: {
  readonly proveedor: ProveedorAPagar | null;
  readonly onCerrar: () => void;
  readonly cajaFija?: CajaFija | undefined;
}): React.ReactElement {
  const { t } = useTranslation();
  const clienteDeConsultas = useQueryClient();
  const textoDeError = useTextoDeError();

  const [cajaElegida, setCajaElegida] = useState('');
  const [medio, setMedio] = useState('efectivo');
  /** Los cheques de la cartera que se entregan. Sólo cuando el medio es cheque. */
  const [endosados, setEndosados] = useState<string[]>([]);
  const [importe, setImporte] = useState('');
  const [aplicado, setAplicado] = useState<Record<string, string> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hecho, setHecho] = useState<string | null>(null);

  // La cartera se pide sólo cuando hace falta: quien paga en efectivo no necesita verla.
  const cartera = useQuery({
    queryKey: ['valores-en-cartera'],
    enabled: pagando !== null && medio === 'cheque',
    queryFn: () => pedir<ValorEnCartera[]>('/valores'),
  });

  const facturas = useQuery({
    queryKey: ['facturas-pendientes', pagando?.terceroId],
    enabled: pagando !== null,
    queryFn: () =>
      pedir<FacturaPendiente[]>(
        `/cuentas-corrientes/proveedores/${pagando?.terceroId ?? ''}/pendientes`,
      ),
  });

  const total = enCentavos(importe);

  const propuesta = useMemo(() => {
    let restante = total;
    const partes: Record<string, string> = {};
    for (const factura of facturas.data ?? []) {
      if (restante <= 0) break;
      const aplicar = Math.min(restante, enCentavos(factura.pendiente));
      partes[factura.movimientoId] = deCentavos(aplicar);
      restante -= aplicar;
    }
    return partes;
  }, [total, facturas.data]);

  const asignado = aplicado ?? propuesta;

  // El efectivo pasa por una caja (E37). Con otro medio no se pregunta: no está en ningún cajón.
  const abiertas = useCajasAbiertas(pagando !== null && medio === 'efectivo');
  const puestoDeCajaId =
    medio === 'efectivo' ? cajaDelEfectivo(cajaFija, cajaElegida, abiertas) : '';
  const faltaLaCaja = medio === 'efectivo' && abiertas.length > 0 && puestoDeCajaId === '';

  const pagar = useMutation({
    mutationFn: () =>
      pedir<{ numero: string; total: string; saldo: string }>(
        '/cuentas-corrientes/ordenes-de-pago',
        {
          method: 'POST',
          headers: { 'Idempotency-Key': globalThis.crypto.randomUUID() },
          cuerpo: {
            terceroId: pagando?.terceroId ?? '',
            pagos: [
              {
                medioDePago: medio,
                importe: deCentavos(total),
                ...(medio === 'cheque' && endosados.length > 0 ? { valores: endosados } : {}),
              },
            ],
            imputaciones: Object.entries(asignado)
              .filter(([, valor]) => enCentavos(valor) > 0)
              .map(([movimientoId, valor]) => ({ movimientoId, importe: valor })),
            ...(puestoDeCajaId === '' ? {} : { puestoDeCajaId }),
          },
        },
      ),
    onSuccess: async (respuesta) => {
      setHecho(
        t('pagos.pagado', {
          numero: respuesta.numero,
          total: respuesta.total,
          saldo: respuesta.saldo,
        }),
      );
      setImporte('');
      setEndosados([]);
      setAplicado(null);
      setError(null);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['cuentas-de-proveedores'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['facturas-pendientes'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['valores-en-cartera'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['movimientos-de-caja'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['arqueo'] });
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  return (
    <Dialogo
      abierto={pagando !== null}
      titulo={t('pagos.orden', { proveedor: pagando?.razonSocial ?? '' })}
      onCerrar={() => {
        setHecho(null);
        setError(null);
        onCerrar();
      }}
      acciones={
        <Boton
          tono="principal"
          cargando={pagar.isPending}
          disabled={total <= 0 || faltaLaCaja}
          onClick={() => {
            pagar.mutate();
          }}
        >
          {t('pagos.registrar')}
        </Boton>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-end gap-2">
          <Selector
            etiqueta={t('cuentas.medioDePago')}
            value={medio}
            onChange={(evento) => {
              setMedio(evento.target.value);
            }}
          >
            {MEDIOS.map((valor) => (
              <option key={valor} value={valor}>
                {t(`venta.${valor}`, valor)}
              </option>
            ))}
          </Selector>
          <Campo
            etiqueta={t('cuentas.importe')}
            inputMode="decimal"
            value={importe}
            onChange={(evento) => {
              setImporte(evento.target.value);
            }}
          />
        </div>

        {medio === 'efectivo' ? (
          <CajaDelEfectivo
            fija={cajaFija}
            abiertas={abiertas}
            valor={cajaElegida}
            onCambiar={setCajaElegida}
          />
        ) : null}

        {/*
          Endosar: entregarle al proveedor cheques que dieron los clientes. **No pasa por el
          banco**, y por eso un mayorista puede pagar con lo que cobró sin esperar la
          acreditación. Se muestra el total de lo elegido al lado del importe porque el servidor
          exige que coincidan —un cheque se endosa entero— y descubrirlo recién al confirmar es
          hacer contar dos veces.
        */}
        {medio !== 'cheque' ? null : (
          <div>
            <p className="text-xs font-medium text-texto-tenue">{t('pagos.endosar')}</p>
            {cartera.isPending ? <Cargando /> : null}
            {cartera.isSuccess && cartera.data.length === 0 ? (
              <p className="mt-1 text-xs text-texto-tenue">{t('pagos.sinCartera')}</p>
            ) : null}
            {cartera.isSuccess && cartera.data.length > 0 ? (
              <>
                <ul className="flex flex-col divide-y divide-borde">
                  {cartera.data.map((valor) => (
                    <li key={valor.id} className="flex items-center gap-3 py-2 text-sm">
                      <input
                        type="checkbox"
                        id={`cheque-${valor.id}`}
                        checked={endosados.includes(valor.id)}
                        onChange={(evento) => {
                          setEndosados((previos) =>
                            evento.target.checked
                              ? [...previos, valor.id]
                              : previos.filter((id) => id !== valor.id),
                          );
                        }}
                      />
                      <label htmlFor={`cheque-${valor.id}`} className="min-w-0 flex-1">
                        {valor.numero} · {valor.banco}
                        <span className="block text-xs text-texto-tenue">
                          {t('pagos.seCobra')}: {valor.fechaDeCobro}
                        </span>
                      </label>
                      <span className="numero">{conDosDecimales(valor.importe)}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-1 text-xs text-texto-tenue">
                  {t('pagos.totalEndosado')}:{' '}
                  <span className="numero">
                    {deCentavos(
                      (cartera.data ?? [])
                        .filter((valor) => endosados.includes(valor.id))
                        .reduce((suma, valor) => suma + enCentavos(valor.importe), 0),
                    )}
                  </span>
                </p>
              </>
            ) : null}
          </div>
        )}

        <div>
          <p className="text-xs font-medium text-texto-tenue">{t('pagos.facturas')}</p>
          {facturas.isPending ? <Cargando /> : null}
          {facturas.isSuccess && facturas.data.length === 0 ? (
            <p className="mt-1 text-xs text-texto-tenue">{t('pagos.sinFacturas')}</p>
          ) : null}
          {facturas.isSuccess && facturas.data.length > 0 ? (
            <ul className="flex flex-col divide-y divide-borde">
              {facturas.data.map((factura) => (
                <li key={factura.movimientoId} className="flex items-center gap-3 py-2 text-sm">
                  <span className="min-w-0 flex-1">
                    {factura.concepto ?? '—'}
                    <span className="block text-xs text-texto-tenue">
                      {t('cuentas.pendiente')}: {conDosDecimales(factura.pendiente)}
                      {factura.vencimiento === null ? '' : ` · ${factura.vencimiento}`}
                    </span>
                  </span>
                  <input
                    aria-label={`${t('cuentas.aImputar')} ${factura.concepto ?? ''}`}
                    inputMode="decimal"
                    className="numero w-28 rounded-marco border border-borde bg-superficie px-2 py-1 text-right text-sm"
                    value={asignado[factura.movimientoId] ?? ''}
                    onChange={(evento) => {
                      setAplicado({ ...asignado, [factura.movimientoId]: evento.target.value });
                    }}
                  />
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        {error === null ? null : (
          <p role="alert" className="text-xs text-mal">
            {error}
          </p>
        )}
        {hecho === null ? null : (
          <p role="status" className="text-xs text-bien">
            {hecho}
          </p>
        )}
      </div>
    </Dialogo>
  );
}
